import { queryOptions, useQuery } from "@tanstack/react-query";

import {
	apiPaths,
	DIRECTORY_RELATED_MIN_LENGTH,
	directoryPageSchema,
	directoryRelatedSchema,
	type DirectoryEntry,
	type DirectoryQuery,
	type DirectoryRelatedQuery,
	type PartyKind,
} from "@ravnar/api-contract";

import { useSettled } from "../lib/settled";
import { ApiError, apiRequest } from "./client";
import { LIST_STALE_TIME_MS } from "./refresh";

// The Directory's two requests: the list, and the parties related to a search. Both read what nothing writes through
// the API, so every query key starts with "directory" and a party added anywhere refreshes both at once.

/**
 * A page of the organization's directory, A→Z by name, filtered by kind and by what the search box holds. Only
 * members with access to the Directory may read it; the API answers everyone else with 403 and a page link it did not
 * make with 400.
 */
export const directoryPageQueryOptions = ({ kind, q, page }: DirectoryQuery = {}) =>
	queryOptions({
		queryKey: ["directory", "page", { kind, q, page }],
		queryFn: ({ signal }) =>
			apiRequest(apiPaths.directory, directoryPageSchema, {
				query: {
					...(kind !== undefined && { kind }),
					...(q !== undefined && q !== "" && { q }),
					...(page !== undefined && { page }),
				},
				signal,
			}),
		// The Directory is a projection a Lambda writes after the record it comes from, so a list that has been sitting
		// a few seconds is asked again the next time the page is opened or the window is come back to.
		staleTime: LIST_STALE_TIME_MS,
		// A refused page link or a refused member will not change on a retry, and the page acts on it straight away.
		retry: (failureCount, error) => failureCount < 1 && !(error instanceof ApiError && error.status < 500),
	});

/**
 * The parties the Directory's knowledge base finds most like a search of three letters or more, best first, of the
 * kind the pills show — never one whose name holds the search, which the list shows already. A knowledge base the API
 * cannot search answers none, rather than an error.
 */
export const directoryRelatedQueryOptions = ({ q, kind }: DirectoryRelatedQuery) =>
	queryOptions({
		queryKey: ["directory", "related", { q, kind }],
		queryFn: ({ signal }) =>
			apiRequest(apiPaths.directoryRelated, directoryRelatedSchema, {
				query: { q, ...(kind !== undefined && { kind }) },
				signal,
			}),
		// The knowledge base is a few seconds behind the list, which is behind the records: asked again as the list is.
		staleTime: LIST_STALE_TIME_MS,
		retry: (failureCount, error) => failureCount < 1 && !(error instanceof ApiError && error.status < 500),
	});

/**
 * How long a search box must rest before the related parties are asked for. Each ask is a search of Bedrock's
 * knowledge base — twenty a second for the whole account — where the list itself is one query of the directory table,
 * so the list follows every keystroke and the related parties the pause after the last one.
 */
const RELATED_SEARCH_DELAY_MS = 300;

/** A search of the Directory as `useRelatedParties` follows it. */
export interface RelatedSearch {
	/** What the search box holds, as it is typed. */
	readonly q: string;
	/** The kind the pills show, when one is chosen. */
	readonly kind?: PartyKind;
	/**
	 * Whether the page of names being shown is the one where the parties whose names hold the search end: the related
	 * parties follow the last of those, and are asked for on no page before it.
	 */
	readonly namesEnd: boolean;
}

/**
 * The related parties a search of the Directory shows after the names that hold it: asked for once the search box has
 * rested, for a search of three letters or more, on the page where the names end, and none anywhere else. The Directory
 * page and the rail's search box both ask through it, so the two are one search.
 */
export function useRelatedParties({ q, kind, namesEnd }: RelatedSearch): readonly DirectoryEntry[] {
	const settled = useSettled(q.trim(), RELATED_SEARCH_DELAY_MS);
	const asks = namesEnd && settled.length >= DIRECTORY_RELATED_MIN_LENGTH;
	const { data } = useQuery({ ...directoryRelatedQueryOptions({ q: settled, kind }), enabled: asks });
	return asks ? (data?.parties ?? []) : [];
}
